/**
 * badgeService — admin client for recognition badge definitions + the
 * admin award endpoint. Mirrors communityApi/certificateService conventions
 * (named exports, unwraps the `ApiResponse<T>` envelope).
 *
 * The admin award path ALSO auto-issues a downloadable certificate on the
 * backend (see CertificateService.issueForBadge) — callers just POST and
 * report success; no separate certificate creation is needed.
 */
import api from './api'
import type { ApiResponse } from '../types'
import type { BadgeResponse, MemberBadgeResponse, AwardBadgeRequest } from './communityApi'

export interface BadgeRequest {
  key?: string
  name: string
  description?: string
  icon?: string
  criteria?: string
  tier?: string
}

export async function listBadgesAdmin(): Promise<BadgeResponse[]> {
  const r = await api.get<ApiResponse<BadgeResponse[]>>('/community/recognition/admin/badges')
  return r.data.data
}

export async function createBadge(req: BadgeRequest): Promise<BadgeResponse> {
  const r = await api.post<ApiResponse<BadgeResponse>>('/community/recognition/admin/badges', req)
  return r.data.data
}

export async function updateBadge(id: number, req: BadgeRequest): Promise<BadgeResponse> {
  const r = await api.put<ApiResponse<BadgeResponse>>(`/community/recognition/admin/badges/${id}`, req)
  return r.data.data
}

export async function deleteBadge(id: number): Promise<void> {
  await api.delete<ApiResponse<void>>(`/community/recognition/admin/badges/${id}`)
}

/**
 * Admin awards a badge to a user. The backend creates the MemberBadge AND
 * auto-generates the certificate in one transaction. Returns the awarded badge.
 */
export async function awardBadgeAdmin(badgeId: number, userId: number): Promise<MemberBadgeResponse> {
  const r = await api.post<ApiResponse<MemberBadgeResponse>>('/community/recognition/admin/award', {
    badgeId, userId,
  } as AwardBadgeRequest)
  return r.data.data
}